import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { useNotifications } from '@revenuewell/uc-notifications-client';
import {
  AppointmentRequestNewEvent,
  AppointmentRequestUpdateEvent
} from '@revenuewell/uc-notifications-client/dist/types/notifications';
import { useHistory } from 'apps/wrapper/src/hooks/use-history-intercept';
import { useLocations } from 'apps/wrapper/src/hooks/use-locations';
import { useWrapperNavigation } from 'apps/wrapper/src/hooks/use-wrapper-navigation';
import AppointmentService from 'apps/wrapper/src/services/appointment/appointment-service';
import { AppointmentRequestStatus } from 'apps/wrapper/src/services/appointment/types/appointment-request-status';
import { AppointmentRequestSummary } from 'apps/wrapper/src/services/appointment/types/appointment-request-summary';
import { PmsWritebackStatus } from 'apps/wrapper/src/services/appointment/types/pms-writeback-status';
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useEventBus } from '../../../../../../hooks/use-event-bus';
import { AppointmentRequestsFilter } from '../../../../../../types/appointment-request/appointment-requests-filter';
import { EventApptRequestDeclined, EventApptRequestsWidget, EventBus } from '../../../../../../types/event-bus';
import { ApptRequestsWidgetRoute, ApptRequestsWidgetRoutePath } from '../../navigation/route-config';
import { ApptRequestsWidgetContext } from './context';

export interface ApptRequestsWidgetProviderProps {
  children: ReactNode;
  widgetEvent: EventApptRequestsWidget | undefined;
}

export const ApptRequestsWidgetProvider: FC<ApptRequestsWidgetProviderProps> = (props) => {
  const { widgetEvent } = props;
  const history = useHistory();
  const { config } = useConfig();
  const { listen } = useNotifications();
  const { showError } = useNotification();
  const { publishToIframes, listen: eventBusListen } = useEventBus();
  const { oidcService, logoutRedirect } = useOidcWithAdmin();
  const { selectedLocation, locations } = useLocations();
  const { activeFrame, windowUrls } = useWrapperNavigation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMoreApptRequests, setIsLoadingMoreApptRequests] = useState<boolean>(false);
  const [allApptRequestsLoaded, setAllApptRequestsLoaded] = useState<boolean>(false);
  const [locationId, setLocationId] = useState<number>(selectedLocation?.id || null!);
  const [locationName, setLocationName] = useState<string>(selectedLocation?.name || '');
  const [routeHistory, setRouteHistory] = useState<ApptRequestsWidgetRoute[]>([]);
  const [route, setRoute] = useState<ApptRequestsWidgetRoute>();
  const [appointmentRequests, setAppointmentRequests] = useState<AppointmentRequestSummary[]>([]);
  const [appointmentRequestsFilter, setAppointmentRequestsFilter] = useState<AppointmentRequestsFilter>();
  const [listScrollTop, setListScrollTop] = useState<number>(0);
  const appointmentService = useMemo(() => new AppointmentService(config, oidcService), [config, oidcService]);

  useEffect(() => {
    if (!widgetEvent || !selectedLocation) return;

    const init = async () => {
      const intialRoute = widgetEvent.data?.path
        ? { path: widgetEvent.data.path, props: widgetEvent.data.props }
        : { path: ApptRequestsWidgetRoutePath.APPT_REQUESTS };

      let locId = selectedLocation.id;
      let locName = selectedLocation.name;

      if (widgetEvent.data?.locationId) {
        locId = widgetEvent.data.locationId;
        locName = locations.find((loc) => loc.id === locId)?.name || '';
      }

      const filterToUse = widgetEvent?.data?.props?.filter || {
        requestStatus: [],
        requestType: [],
        source: []
      };

      setAppointmentRequestsFilter(filterToUse);
      setLocationId(locId);
      setLocationName(locName);
      goTo(intialRoute);
    };

    init();
  }, [widgetEvent, selectedLocation]);

  useEffect(() => {
    if (!locationId || !appointmentRequestsFilter) return;

    (async () => {
      setIsLoading(true);
      const requests = await loadAppointmentRequests(locationId);
      setAppointmentRequests(requests);
      setIsLoading(false);
    })();
  }, [appointmentRequestsFilter, locationId]);

  useEffect(() => {
    setListScrollTop(0);
  }, [appointmentRequestsFilter, locationId]);

  useEffect(() => {
    const apptRequestUpdateEvent = listen('appointment-request-update', (event: any) => {
      const locId = event?.locationId || event?.locationid;

      if (locId != locationId) return;

      const { data } = event as AppointmentRequestUpdateEvent;

      updateApptRequestStatus(data.id, data.status);
    });

    const apptRequestCeateEvent = listen('appointment-request-new', (event: any) => {
      const locId = event?.locationId || event?.locationid;

      if (locId != locationId) return;

      const { data } = event as AppointmentRequestNewEvent;

      handleCreateApptRequestNotification(data.id);
    });

    return () => {
      apptRequestUpdateEvent();
      apptRequestCeateEvent();
    };
  }, [listen]);

  useEffect(() => {
    const declineRequestListener = eventBusListen('appt-request-declined', (event: EventBus) => {
      const { data } = event as EventApptRequestDeclined;

      updateApptRequestStatus(data.id, AppointmentRequestStatus.Deleted);

      if (route?.path === ApptRequestsWidgetRoutePath.REQUEST_DETAILS) goBack();
    });

    const acceptRequestListener = eventBusListen('appt-request-accepted', (event: EventBus) => {
      const { data } = event as EventApptRequestDeclined;

      updateApptRequestStatus(data.id, AppointmentRequestStatus.Unconfirmed);

      if (route?.path === ApptRequestsWidgetRoutePath.REQUEST_DETAILS) goBack();
    });

    return () => {
      declineRequestListener();
      acceptRequestListener();
    };
  }, [eventBusListen, route]);

  // There is no Notification system for the PMS Status update,
  // so use polling as an alternative
  useEffect(() => {
    const interval = setInterval(async () => {
      if (isLoading || isLoadingMoreApptRequests) return;

      const pendingPmsRequests = appointmentRequests
        .filter((x) => x.pmsWritebackStatus == PmsWritebackStatus.Pending)
        .map((x) => x.id);

      if (!pendingPmsRequests.length) return;

      try {
        const actualStatuses = await appointmentService.getAppointmentRequestStatuses(locationId, pendingPmsRequests);

        setAppointmentRequests((prev) =>
          prev.map((request) => {
            const status = actualStatuses.find((x) => x.id === request.id);
            return status ? { ...request, pmsWritebackStatus: status.pmsWritebackStatus } : request;
          })
        );
      } catch (error) {
        console.error('Error checking PMS writeback status:', error);
        showError('Unable to check PMS writeback status.');
      }
    }, 2 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isLoading, isLoadingMoreApptRequests, locationId, appointmentService]);

  const updateApptRequestStatus = (requestId: number, status: AppointmentRequestStatus) => {
    setAppointmentRequests((prev) =>
      prev.map((request) =>
        request.id == requestId ? { ...request, status, updatedDate: new Date().toISOString() } : request
      )
    );
  };

  const markRequestAsRead = async (id: number) => {
    const appointmentRequest = appointmentRequests.find((req) => req.id === id);
    if (!appointmentRequest || appointmentRequest.status != AppointmentRequestStatus.New) return;

    try {
      await appointmentService.markAsRead(id, locationId);
      updateApptRequestStatus(id, AppointmentRequestStatus.Pending);
    } catch {
      console.error('Failed to mark appointment request as read');
    }
  };

  const loadMoreApptRequests = useCallback(async () => {
    setIsLoadingMoreApptRequests(true);

    const latestLoadedDate = appointmentRequests[appointmentRequests.length - 1].createdDate;
    const moreRequests = await loadAppointmentRequests(locationId, new Date(latestLoadedDate));
    const dedupedRequests = moreRequests.filter((req) => !appointmentRequests.find((r) => r.id === req.id));

    if (!dedupedRequests.length) setAllApptRequestsLoaded(true);
    else setAppointmentRequests((prev) => [...prev, ...dedupedRequests]);

    setIsLoadingMoreApptRequests(false);
  }, [locationId, appointmentRequests]);

  const loadAppointmentRequests = useCallback(
    async (locId: number, startDate?: Date) => {
      try {
        const filter = appointmentRequestsFilter ?? { requestStatus: [], requestType: [], source: [] };
        return await appointmentService.getAppointmentRequests(locId, {
          lastDate: (startDate ?? new Date()).toISOString(),
          numberOfRecords: 30,
          requestStatusFilter: filter.requestStatus,
          requestTypeFilter: filter.requestType,
          requestSourceFilter: filter.source
        });
      } catch (error: any) {
        if (error?.status === 401 || error?.status === 403) {
          logoutRedirect();
        } else {
          console.error(error);
          showError('Error loading appointment requests.');
        }

        return [];
      }
    },
    [appointmentRequestsFilter]
  );

  const goToCalendarApp = async (): Promise<void> =>
    new Promise((resolve) => {
      if (activeFrame !== 'scheduler') history.push(windowUrls.scheduler);

      if (history.location.pathname.includes('/calendar')) {
        resolve();

        return;
      }

      setTimeout(() => {
        publishToIframes({
          messageType: 'set-frame-path',
          data: {
            pathname: '/calendar'
          }
        });
        resolve();
      }, 100);
    });

  const handleCreateApptRequestNotification = useCallback(
    async (id: number) => {
      try {
        const newApptRequest = await appointmentService.getAppointmentRequestDetails(id, locationId);

        setAppointmentRequests((prev) => [newApptRequest, ...prev]);
      } catch (error) {
        console.error('Error loading new appointment request details:', error);
        showError('Unable to load appointment request.');
      }
    },
    [appointmentService, setAppointmentRequests]
  );

  const goTo = (newRoute: ApptRequestsWidgetRoute) => {
    if (newRoute.path === route?.path) return;

    setRouteHistory((prev) => [...prev, newRoute]);
    setRoute(newRoute);
  };

  const goBack = (fallbackToMainPage?: boolean) => {
    if (routeHistory.length > 1) {
      const newRoute = routeHistory[routeHistory.length - 2];
      setRouteHistory((prev) => prev.slice(0, -1));
      setRoute(newRoute);
    } else {
      setRoute({
        path: ApptRequestsWidgetRoutePath.APPT_REQUESTS
      });
    }
  };

  const contextValue = useMemo(
    () => ({
      events: {
        widgetEvent
      },
      routeHistory,
      route,
      goTo,
      goBack,
      goToCalendarApp,
      isLoading,
      isLoadingMoreApptRequests,
      locationId,
      locationName,
      appointmentRequests,
      allApptRequestsLoaded,
      loadMoreApptRequests,
      appointmentRequestsFilter,
      setAppointmentRequestsFilter,
      markRequestAsRead,
      listScrollTop,
      setListScrollTop
    }),
    [
      widgetEvent,
      routeHistory,
      route,
      goTo,
      goBack,
      goToCalendarApp,
      isLoading,
      isLoadingMoreApptRequests,
      locationId,
      locationName,
      appointmentRequests,
      allApptRequestsLoaded,
      loadMoreApptRequests,
      appointmentRequestsFilter,
      setAppointmentRequestsFilter,
      markRequestAsRead,
      listScrollTop,
      setListScrollTop
    ]
  );

  return <ApptRequestsWidgetContext.Provider value={contextValue}>{props.children}</ApptRequestsWidgetContext.Provider>;
};
